import {styled} from '@linaria/react';
import React, {HTMLAttributes, type JSX} from 'react';
import {COLORS} from '../../../constants/colors';

interface RippleProps
  extends HTMLAttributes<HTMLSpanElement | HTMLButtonElement> {
  as?:
    | keyof JSX.IntrinsicElements
    | React.ComponentType<React.PropsWithChildren>;
  onClick?: React.MouseEventHandler;
  className?: string;
  styles?: React.CSSProperties;
  hidden?: boolean;
  disabled?: boolean;
  disableRipple?: boolean;
  disableTabNavigation?: boolean;
  theme?: 'light' | 'dark';
}

export const Ripple = React.forwardRef<
  HTMLElement,
  React.PropsWithChildren<RippleProps>
>(function Ripple(
  {
    children,
    hidden,
    theme = 'light',
    disableTabNavigation,
    disableRipple,
    ...rest
  },
  ref,
) {
  if (hidden) {
    return null;
  }

  return (
    <RippleWrapper
      ref={ref as React.ForwardedRef<HTMLDivElement>}
      data-disabled={rest.disabled}
      tabIndex={disableTabNavigation ? -1 : undefined}
      {...rest}
    >
      {children}
      {!rest.disabled && !disableRipple && <RippleElement $theme={theme} />}
    </RippleWrapper>
  );
});

const RippleWrapper = styled.div`
  position: relative;
  overflow: hidden;
  display: inline-block;
  cursor: pointer;
  -webkit-tap-highlight-color: transparent;
  outline: none;
  border: 0;

  &[data-disabled='true'] {
    opacity: 0.6;
    pointer-events: none;
    cursor: default;
  }
`;

const RippleElement = styled.div<{$theme: RippleProps['theme']}>`
  position: absolute;
  top: 50%;
  left: 50%;
  width: 100px;
  height: 100px;
  border-radius: 50%;
  background-color: ${({$theme}) =>
    $theme === 'dark' ? COLORS.Grey1 : COLORS.White};
  transform: translate(-50%, -50%) scale(0);
  opacity: 0;
  pointer-events: none;

  ${RippleWrapper}:active & {
    animation: cssRippleEffect 850ms cubic-bezier(0.25, 0.8, 0.25, 1);
  }

  @keyframes cssRippleEffect {
    0% {
      transform: translate(-50%, -50%) scale(0);
      opacity: 0.6;
    }
    100% {
      transform: translate(-50%, -50%) scale(3);
      opacity: 0;
    }
  }
`;
